import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { X, Clock, Eye, EyeOff } from 'lucide-react';
import type { NotificationDocument } from '@/services/shared/notificationService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface NotificationItemProps {
  notification: NotificationDocument;
  onMarkAsRead: (notificationId: string) => void;
  onDismiss: (notificationId: string) => void;
  onNavigate?: (notification: NotificationDocument) => void;
  className?: string;
}

/**
 * NotificationItem component - Enhanced individual notification with animations and better UX
 *
 * Features:
 * - Smooth hover animations and transitions
 * - Enhanced visual feedback for interactions
 * - Better accessibility with proper ARIA labels
 * - Optimized performance with memoized callbacks
 * - Improved visual hierarchy and typography
 * - Interactive elements with proper focus states
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDismiss,
  onNavigate,
  className = ''
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActioning, setIsActioning] = useState(false);
  const [mentionerName, setMentionerName] = useState<string>('Someone');
  const [mentionerInitials, setMentionerInitials] = useState<string>('?');
  const [mentionerAvatarColor, setMentionerAvatarColor] = useState<string>('#9333ea');
  const [mentionerAvatarUrl, setMentionerAvatarUrl] = useState<string | null>(null);

  // Memoized handlers for better performance
  const handleClick = useCallback(() => {
    // Mark as read when clicked
    if (notification.status === 'unread') {
      onMarkAsRead(notification.id!);
    }
    
    // Navigate to source if callback provided
    if (onNavigate) {
      onNavigate(notification);
    }
  }, [notification.status, notification.id, onMarkAsRead, onNavigate]);

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the main click handler
    setIsActioning(true);
    onDismiss(notification.id!);
    // Reset actioning state after animation
    setTimeout(() => setIsActioning(false), 300);
  }, [notification.id, onDismiss]);

  const handleToggleRead = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsActioning(true);
    onMarkAsRead(notification.id!);
    // Reset actioning state after animation
    setTimeout(() => setIsActioning(false), 300);
  }, [notification.id, onMarkAsRead]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  // Fetch mentioner profile for avatar
  useEffect(() => {
    const fetchMentionerProfile = async () => {
      if (notification.type === 'mention' && notification.mentionData?.mentionedBy) {
        try {
          // Extract document ID from format like "User:25"
          const mentionerId = notification.mentionData.mentionedBy.replace('User:', '');
          
          // Get document directly from system_users collection
          const userDocRef = doc(db, 'system_users', mentionerId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const name = userData.employee_data?.name ||
                        `${userData.employee_data?.first_name || ''} ${userData.employee_data?.last_name || ''}`.trim() ||
                        'Unknown User';
            
            setMentionerName(name);
            
            // Get cached avatar URL
            const avatarUrl = userData.employee_data?.cached_avatar_url;
            setMentionerAvatarUrl(avatarUrl || null);
            
            // Generate initials from name (fallback)
            const nameParts = name.split(' ');
            const initials = nameParts.length >= 2
              ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
              : nameParts[0][0]?.toUpperCase() || '?';
            setMentionerInitials(initials);
            
            // Generate color from employee_id for consistency (fallback)
            const colors = ['#9333ea', '#2563eb', '#059669', '#dc2626', '#ea580c', '#ca8a04'];
            const colorIndex = (userData.employee_id || 0) % colors.length;
            setMentionerAvatarColor(colors[colorIndex]);
          }
        } catch (error) {
          console.error('Error fetching mentioner profile:', error);
        }
      }
    };

    fetchMentionerProfile();
  }, [notification.type, notification.mentionData?.mentionedBy]);

  // Memoized timestamp formatting for performance
  const formattedTimestamp = useMemo(() => {
    if (!notification.createdAt) return '';
    
    try {
      // Handle Firestore Timestamp
      const date = notification.createdAt.toDate ? notification.createdAt.toDate() : new Date(notification.createdAt as any);
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;
      
      // For older notifications, show the actual date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return '';
    }
  }, [notification.createdAt]);

  // Extract domain from entryDisplayName or pageUrl
  const domainName = useMemo(() => {
    if (notification.mentionData?.pageUrl) {
      // Extract domain from URL like "/intel/overview" -> "Intel"
      const pathParts = notification.mentionData.pageUrl.split('/').filter(Boolean);
      if (pathParts.length > 0) {
        return pathParts[0].charAt(0).toUpperCase() + pathParts[0].slice(1);
      }
    }
    
    // Fallback to entryDisplayName parsing
    if (notification.entryDisplayName) {
      const parts = notification.entryDisplayName.split(' - ');
      if (parts.length > 0) {
        return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      }
    }
    
    return 'Workspace';
  }, [notification.mentionData?.pageUrl, notification.entryDisplayName]);

  // Format page name from entryDisplayName or URL
  const pageName = useMemo(() => {
    if (notification.mentionData?.pageUrl) {
      const pathParts = notification.mentionData.pageUrl.split('/').filter(Boolean);
      if (pathParts.length > 1) {
        return pathParts.slice(1).map(p =>
          p.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
        ).join(' > ');
      }
    }
    
    // Fallback to entryDisplayName
    if (notification.entryDisplayName) {
      const parts = notification.entryDisplayName.split(' - ');
      if (parts.length > 2) {
        return parts.slice(1, 3).join(' - ');
      }
    }
    
    return 'Page';
  }, [notification.mentionData?.pageUrl, notification.entryDisplayName]);

  // Clean and truncate message text
  const cleanedMessage = useMemo(() => {
    let text = notification.description;
    
    // Remove mention tags like @[Name](email) but keep the name
    text = text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '');
    
    // Remove "mentioned you in:" prefix if present
    text = text.replace(/^.*mentioned you in:\s*/i, '');
    
    // Remove any leading/trailing quotes
    text = text.replace(/^["']|["']$/g, '').trim();
    
    // Truncate to ~50 characters with ellipsis
    const maxLength = 50;
    if (text.length > maxLength) {
      text = text.substring(0, maxLength).trim() + '...';
    }
    
    return text || 'No message content';
  }, [notification.description]);

  // Format title: "(Mentioner) mentioned you in (Domain)"
  const formattedTitle = useMemo(() => {
    if (notification.type === 'mention') {
      return `${mentionerName} mentioned you in ${domainName}`;
    }
    return notification.title;
  }, [notification.type, mentionerName, domainName, notification.title]);

  const isUnread = notification.status === 'unread';

  return (
    <div
      className={`
        relative p-4 cursor-pointer transition-all duration-200 ease-in-out
        hover:bg-gray-50
        ${isHovered ? 'shadow-sm' : ''}
        ${isActioning ? 'scale-98 opacity-75' : 'scale-100 opacity-100'}
        ${className}
      `}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="button"
      tabIndex={0}
      aria-label={`${notification.title}. ${isUnread ? 'Unread' : 'Read'} notification.`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex items-start space-x-3">
        {/* User Avatar */}
        <div className="flex-shrink-0 mt-0.5 relative">
          <div
            className={`
              h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-sm
              transition-transform duration-200 ${isHovered ? 'scale-110' : 'scale-100'}
              ${mentionerAvatarUrl ? 'overflow-hidden' : ''}
            `}
            style={{ backgroundColor: mentionerAvatarUrl ? 'transparent' : mentionerAvatarColor }}
          >
            {mentionerAvatarUrl ? (
              <img
                src={mentionerAvatarUrl}
                alt={mentionerName}
                className="h-full w-full object-cover rounded-full"
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent) {
                    parent.style.backgroundColor = mentionerAvatarColor;
                    parent.classList.remove('overflow-hidden');
                    const textNode = document.createTextNode(mentionerInitials);
                    parent.appendChild(textNode);
                  }
                }}
              />
            ) : (
              mentionerInitials
            )}
          </div>
          {isUnread && (
            <div className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 bg-purple-500 rounded-full border-2 border-white"></div>
          )}
        </div>

        {/* Enhanced Content */}
        <div className="flex-1 min-w-0">
          {/* Simplified Title */}
          <div className="flex items-start justify-between">
            <h4 className={`text-sm font-medium leading-tight ${isUnread ? 'text-gray-900' : 'text-gray-600'}`}>
              {formattedTitle}
            </h4>
          </div>

          {/* Cleaned and Truncated Message */}
          <p className={`text-sm mt-1 leading-relaxed ${isUnread ? 'text-gray-700' : 'text-gray-500'}`}>
            {cleanedMessage}
          </p>

          {/* Page Link */}
          {pageName && (
            <div className="mt-2">
              <a
                href={notification.mentionData?.pageUrl || '#'}
                className="text-xs text-purple-600 hover:text-purple-800 hover:underline font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate(notification);
                  }
                }}
              >
                {pageName}
              </a>
            </div>
          )}

          {/* Enhanced Timestamp */}
          <div className="flex items-center mt-2 text-xs text-gray-400">
            <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
            <span>{formattedTimestamp}</span>
          </div>
        </div>
      </div>

      {/* Enhanced Action Buttons - Shows on hover */}
      {isHovered && (
        <div className="absolute top-4 right-4 flex items-center space-x-1">
          {/* Toggle read/unread button */}
          <button
            onClick={handleToggleRead}
            className="p-1.5 rounded-full hover:bg-white hover:shadow-md transition-all duration-200 group"
            aria-label={isUnread ? "Mark as read" : "Mark as unread"}
            title={isUnread ? "Mark as read" : "Mark as unread"}
          >
            {isUnread ? (
              <Eye className="h-4 w-4 text-gray-400 group-hover:text-purple-600" />
            ) : (
              <EyeOff className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>
          
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-full hover:bg-red-50 hover:shadow-md transition-all duration-200 group"
            aria-label="Dismiss notification"
            title="Dismiss notification"
          >
            <X className="h-4 w-4 text-gray-400 group-hover:text-red-600" />
          </button>
        </div>
      )}
    </div>
  );
};

